/**
 * Which journey does the citizen share with the inviting company? (R5)
 *
 * Renders what `useAcceptJourneyChooser` decided: the journeys the citizen may
 * share — each drawn so two on one route can be told apart (R8) — and "a new
 * journey for <company>", the server's suggestion preselected. With nothing to
 * share it says a new journey will be built, and asks nothing. A TestIt
 * invitation asks no such question (D3): its tests are listed instead
 * (`InvitationAskedTests`). Shared by the dashboard review dialog and
 * `/accept-invite`.
 */
import { AlertCircle, Loader2, RefreshCw } from '@tonnie/assets';
import { useCountryName, useLocale, useTranslation } from '@tonnie/locale';
import {
  sameAcceptJourneyChoice,
  type AcceptJourneyChoice,
  type AcceptJourneyChooser,
} from '@tonnie/sdk';
import { Button, cn, Label, RadioGroup, RadioGroupItem } from '@tonnie/ui';

import { JourneyNameLine } from '@/features/platform/journey/components/JourneyNameLine';

import { InvitationAskedTests } from './InvitationAskedTests';

/** The radio value of one choice — a journey id, or the new journey. */
const NEW_VALUE = 'new';
const valueOf = (choice: AcceptJourneyChoice) => ('new' in choice ? NEW_VALUE : choice.journeyId);
const choiceOf = (value: string): AcceptJourneyChoice =>
  value === NEW_VALUE ? { new: true } : { journeyId: value };

export function InvitationJourneyChoice({
  chooser,
  companyName,
  destinationCountry,
  disabled,
}: {
  chooser: AcceptJourneyChooser;
  companyName: string;
  /** The invitation's destination, when it names one. */
  destinationCountry: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const countryName = useCountryName();
  const { state } = chooser;

  switch (state.kind) {
    case 'loading':
      return (
        <div
          className="flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="invitation-journey-choice"
          data-choice-state="loading"
        >
          <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t('invitations.choice.checking')}
        </div>
      );
    case 'error':
      return (
        <div
          className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
          role="alert"
          data-testid="invitation-journey-choice"
          data-choice-state="error"
        >
          <AlertCircle
            className="w-4 h-4 mt-0.5 text-destructive-accessible shrink-0"
            aria-hidden="true"
          />
          <div className="flex-1 space-y-2">
            <p className="text-sm">{t('invitations.choice.loadFailed')}</p>
            <Button type="button" size="sm" variant="outline" onClick={chooser.retry}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              {t('common.retry')}
            </Button>
          </div>
        </div>
      );
    case 'blocked':
      return (
        <p
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
          role="alert"
          data-testid="invitation-journey-choice"
          data-choice-state="blocked"
        >
          {t('invitations.choice.blocked', { count: state.cap, company: companyName })}
        </p>
      );
    case 'tests':
      return (
        <InvitationAskedTests
          tests={state.tests}
          companyName={companyName}
          completed={state.completed}
          sharesCheckup={state.sharesCheckup}
        />
      );
    case 'choose': {
      if (state.candidates.length === 0) {
        return (
          <p
            className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
            data-testid="invitation-journey-choice"
            data-choice-state="new-only"
          >
            {destinationCountry
              ? t('invitations.choice.newOnly', {
                  company: companyName,
                  country: countryName(destinationCountry),
                })
              : t('invitations.choice.newOnlyNoCountry', { company: companyName })}
          </p>
        );
      }
      const selected = valueOf(state.selected);
      return (
        <fieldset
          className="space-y-3"
          data-testid="invitation-journey-choice"
          data-choice-state="choose"
          data-choice-selected={selected}
        >
          <legend className="text-sm font-medium mb-1">
            {t('invitations.choice.legend', { company: companyName })}
          </legend>
          <RadioGroup
            value={selected}
            onValueChange={(value) => chooser.select(choiceOf(value))}
            disabled={disabled}
            className="gap-2"
          >
            {state.candidates.map((journey) => {
              const id = `invitation-journey-${journey.id}`;
              const on = sameAcceptJourneyChoice(state.selected, { journeyId: journey.id });
              return (
                <Label
                  key={journey.id}
                  htmlFor={id}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors font-normal',
                    on
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40 hover:bg-muted/30'
                  )}
                  data-testid={`invitation-journey-option-${journey.id}`}
                >
                  <RadioGroupItem id={id} value={journey.id} className="mt-0.5" />
                  <span className="space-y-1 min-w-0">
                    <span className="block text-sm font-semibold text-foreground">
                      {journey.originCountry
                        ? t('invitations.choice.route', {
                            origin: countryName(journey.originCountry),
                            destination: countryName(journey.destinationCountry),
                          })
                        : countryName(journey.destinationCountry)}
                    </span>
                    <JourneyNameLine label={journey.label} startedAt={journey.startedAt} />
                    {journey.isCompanyJourney && (
                      <span className="block text-xs text-muted-foreground">
                        {t('invitations.choice.companyJourney', { company: companyName })}
                      </span>
                    )}
                    {journey.otherCompanies.length > 0 && (
                      <span className="block text-xs text-muted-foreground">
                        {t('invitations.choice.sharedWith', {
                          companies: journey.otherCompanies.join(', '),
                        })}
                      </span>
                    )}
                    {/* Only when accepting grants the CheckUp — the server's
                        answer, never inferred from the journey carrying one. */}
                    {state.sharesCheckup &&
                      journey.checkupCompletedAt &&
                      !journey.isCompanyJourney && (
                        <span className="block text-xs text-muted-foreground leading-relaxed">
                          {t('invitations.choice.checkupShared', {
                            company: companyName,
                            date: formatDate(journey.checkupCompletedAt),
                          })}
                        </span>
                      )}
                  </span>
                </Label>
              );
            })}
            <Label
              htmlFor="invitation-journey-new"
              className={cn(
                'flex items-start gap-3 rounded-xl border p-4 transition-colors font-normal',
                !state.canCreate
                  ? 'border-dashed border-border bg-muted/30 cursor-not-allowed'
                  : selected === NEW_VALUE
                    ? 'border-primary bg-primary/5 cursor-pointer'
                    : 'border-border hover:border-primary/40 hover:bg-muted/30 cursor-pointer'
              )}
              data-testid="invitation-journey-option-new"
            >
              <RadioGroupItem
                id="invitation-journey-new"
                value={NEW_VALUE}
                disabled={!state.canCreate}
                className="mt-0.5"
              />
              <span className="space-y-1">
                <span className="block text-sm font-semibold text-foreground">
                  {t('invitations.choice.newTitle', { company: companyName })}
                </span>
                <span className="block text-xs text-muted-foreground leading-relaxed">
                  {state.canCreate
                    ? t('invitations.choice.newBody', { company: companyName })
                    : t('invitations.choice.newCapped', { count: state.cap })}
                </span>
              </span>
            </Label>
          </RadioGroup>
        </fieldset>
      );
    }
    default: {
      const unhandled: never = state;
      return unhandled;
    }
  }
}
